/**
 * Invitation API — token-based account activation for admins and teachers.
 */

/// <reference lib="dom" />

import { handleApiCall } from './errors';
import { getAccessToken } from './auth-token';

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables');
}

export interface SendInviteRequest {
  email: string;
  role: 'admin' | 'teacher';
  schoolId: string;
  schoolName: string;
  name?: string;
  /** teachers.id for teacher invites */
  refId?: string;
}

export interface SendInviteResult {
  success: boolean;
  /** true when the email is already registered — caller should handle inline */
  emailExists: boolean;
}

/**
 * Send an account activation invite email.
 * Called after school registration (admin) or teacher registration (teacher).
 */
export async function sendInviteDirect(req: SendInviteRequest): Promise<SendInviteResult> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error('Not authenticated');

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        email: req.email,
        role: req.role,
        schoolId: req.schoolId,
        schoolName: req.schoolName,
        name: req.name,
        refId: req.refId,
      }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error ?? 'Failed to send invite');
    return data as SendInviteResult;
  });
}

/**
 * Validate an invitation token (used on the activate-account page to pre-fill info).
 * Uses the anon key — RLS policy allows public SELECT on invitations.
 */
export async function getInvitationByToken(token: string): Promise<{
  email: string;
  role: 'admin' | 'teacher';
  schoolId: string;
  name: string | null;
  valid: boolean;
  error?: string;
}> {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/invitations?token=eq.${token}&select=email,role,school_id,name,expires_at,used`,
    {
      headers: {
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
    }
  );

  if (!response.ok) return { email: '', role: 'teacher', schoolId: '', name: null, valid: false, error: 'Network error' };

  const rows = await response.json();
  if (!rows || rows.length === 0) return { email: '', role: 'teacher', schoolId: '', name: null, valid: false, error: 'Invalid link' };

  const inv = rows[0];
  if (inv.used) return { email: inv.email, role: inv.role, schoolId: inv.school_id, name: inv.name, valid: false, error: 'Link already used' };
  if (new Date(inv.expires_at) < new Date()) return { email: inv.email, role: inv.role, schoolId: inv.school_id, name: inv.name, valid: false, error: 'Link expired' };

  return { email: inv.email, role: inv.role, schoolId: inv.school_id, name: inv.name, valid: true };
}

/**
 * Activate an account using an invitation token.
 * Does NOT require an existing session — called by unauthenticated users.
 */
export async function activateAccountDirect(token: string, password: string, name?: string): Promise<{ success: boolean; role: string }> {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/activate-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ token, password, name }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error ?? `Activation failed (${response.status})`);
  }
  return data;
}
